import React, {
	FunctionComponent,
	Reducer,
	useEffect,
	useReducer,
	useState,
	useRef,
} from 'react';
import { bearingSearchTableHeads, text, leftAlignedHeaders } from '../../data';
import TableLoader from '../TableLoader';
import { generateId, getTooltip, shouldBeCenterAligned } from '../../utils/engineering-tools';
import { View } from '../../types/types';
import SearchPagination from '../../../components/bearing-search/components/SearchPagination';
import Loader from '../../../components/bearing-search/components/Loader';
import bearingSearchReducer, {
	initialSearchState,
	BearingSearchState,
	BearingSearchAction,
} from './reducer';
import SearchPaginationLabel from '../../../components/bearing-search/components/SearchPaginationLabel';
import {
	BearingInfo,
	BearingKey,
	BearingPayload,
	BearingSaveItem,
	BearingSortPayload,
	BearingTableHead,
	BearingTypeCode,
	TimkenUnit,
} from '../../../components/bearing-search/types/types';
import {
	COMPARE_SIZE,
	compareBearingsUrl,
	getBearingDetailsUrl,
	guidedHelpUrls,
	reduceArrayData,
} from '../../../components/bearing-search/api/api';
import { bearingSortableKeys } from '../../../components/bearing-search/data';
import {
	analyticsData,
	getBearingImpressionData,
	trackBearingListImpression,
} from '../../utils/analytics';
import { trackProductClick } from '../../../analytics/engineering-tools';
import {
	login,
	register,
	isEnabled as ssoIsEnabled,
	isAuthenticated as ssoIsAuthenticated,
	isAuthorized as ssoIsAuthorized,
} from '../../../sso';
import SortButton from '../SortButton';
import Tooltip from '../../conversion-tool/Utils/Tooltip';
import AvailabilityBar from '../availability-bar/AvailabilityBar';
import CheckboxPlus from '../../../my-dashboard/components/CheckboxPlus';
import SaveMultiBearingDropdown from '../../../my-dashboard/save-to-project/bearings/SaveMultiBearingDropdown';
import Icon from '../../../components/common/Icon';
import AuthenticationBar from '../authentication-bar/AuthenticationBar';

interface BearingSearchResultsProps {
	unit: TimkenUnit;
	data: BearingInfo[];
	totalRecords: number;
	loading?: boolean;
	onRefine?: (payload: Partial<BearingPayload>) => void;
	bearingTypeListForColumn: Array<any>;
	updatedBearingSearchTableHeads: Array<any>;
}

const BearingSearchResults: FunctionComponent<BearingSearchResultsProps> = ({
	unit,
	data,
	totalRecords,
	loading,
	onRefine,
	bearingTypeListForColumn,
	updatedBearingSearchTableHeads,
}) => {
	const [selectedBearings, setSelectedBearings] = useState<BearingSaveItem[]>([]);
	const [compareUrl, setCompareUrl] = useState('');
	const [refinementState, dispatch] = useReducer<
		Reducer<BearingSearchState, BearingSearchAction>
	>(bearingSearchReducer, initialSearchState);
	const isInComparedData = (marketPlaceDescription: string) =>
		refinementState.comparedData.some(
			(bearing) => bearing.marketPlaceDescription === marketPlaceDescription,
		);
	const isSsoEnabled = ssoIsEnabled();
	const isAuthenticated = ssoIsAuthenticated();
	const isAuthorized = ssoIsAuthorized();
	const ballBearingTypes = ['BB', 'RBB', 'ACBB'];
	const tableBodyRef = useRef<HTMLTableSectionElement>(null);
	const gridViewRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		dispatch({
			type: 'update_total_records',
			payload: totalRecords,
		});

		if (!data.length) {
			dispatch({
				type: 'reset',
			});
		} else {
			const { currentPage, pageSize } = refinementState;

			trackBearingListImpression(
				data,
				currentPage,
				pageSize,
				analyticsData.bearingSearch.name,
			);
		}
	}, [data]);

	// Paginate and sort
	useEffect(() => {
		if (data.length && onRefine) {
			const { sortKey, sortDirection, currentPage } = refinementState;
			let sortOptions: BearingSortPayload | undefined;

			if (sortKey && bearingSortableKeys[sortKey]) {
				sortOptions = {
					sortDirection,
					sortExpression: bearingSortableKeys[sortKey]?.[unit],
				};
			}

			onRefine({
				...sortOptions,
				pageNumber: currentPage,
			});
		}
	}, [
		refinementState.sortKey,
		refinementState.sortDirection,
		refinementState.currentPage,
	]);

	// Update compare url
	useEffect(() => {
		const bearings = refinementState.comparedData;

		if (bearings.length > 1) {
			const url = new URL(compareBearingsUrl);

			bearings.forEach((bearing, index) => {
				const {
					worldAssemblyPartNumber,
					marketPlaceDescription: description,
					bearingType: types,
					bearingSubType: subtypes,
					innerRingPartNumber,
					outerRingPartNumber,
					brand,
				} = bearing;

				let partNumber = worldAssemblyPartNumber || '';
				let cupPartNumber = '';

				const marketPlaceDescription = description || '';
				const bearingType = reduceArrayData(types) as BearingTypeCode;
				const bearingSubType = reduceArrayData(subtypes);

				if (bearingType === BearingTypeCode.TRB) {
					partNumber = reduceArrayData(innerRingPartNumber);
					cupPartNumber = reduceArrayData(outerRingPartNumber);
				}

				url.searchParams.append(
					`bearings[${index}][bearing_type]`,
					bearingType,
				);

				url.searchParams.append(
					`bearings[${index}][bearing_subtype]`,
					bearingSubType,
				);

				url.searchParams.append(`bearings[${index}][part_number]`, partNumber);

				url.searchParams.append(
					`bearings[${index}][cup_part_number]`,
					cupPartNumber,
				);

				url.searchParams.append(
					`bearings[${index}][marketplace_description]`,
					marketPlaceDescription,
				);

				url.searchParams.append(`bearings[${index}][brand]`, brand || '');

				url.searchParams.append('unit', unit);

				setCompareUrl(url.toString());
			});
		}
	}, [refinementState.comparedData]);

	const createBearingSaveItem = (item: BearingInfo): BearingSaveItem => {
		const thumbnailImageUrls = window._timken?.bearingTypeThumbnails || {};
		return {
			bearingTypeDescription: item.typeDescription || '',
			bearingTypeImageUrl: item.partType ? thumbnailImageUrls[item.partType[0]] : '',
			bearingWidth: item.bearingWidth?.toString() || '',
			bore: item.bore?.toString() || '',
			marketPlaceDescription: item.marketPlaceDescription || '',
			materialNumber: item.materialNumber?.[0] || '',
			outerDiameter: item.outerDiameter?.toString() || '',
		};
	};

	const handleProductClick = (item: BearingInfo, index: number) => {
		const { currentPage, pageSize } = refinementState;

		const product = getBearingImpressionData(
			item,
			index,
			currentPage,
			pageSize,
			analyticsData.bearingSearch.name,
		);

		trackProductClick(product);
	};

	const handleSignIn = (e) => {
		e.preventDefault();
		login();
	};

	const handleSignUp = (e) => {
		e.preventDefault();
		register({ appId: 'pa' });
	};

	const profileUrl = window?._timken?.profileUrl as string || '';

	const bearingTypeList: string[] = [];
	const handleSticky = () => {
		data.forEach((item) => {
			bearingTypeList.push(item?.bearingType[0]);
		});

		setTimeout(() => {
			let prevThWidth = 0;
			jQuery('th.frozen-th').each(function (index, item) {
				jQuery(item).css('left', prevThWidth);
				prevThWidth += jQuery(item).outerWidth() ?? 0;
			});

			jQuery('tbody tr').each(function (index, item) {
				let prevTdWidth = 0;
				jQuery(item).find('td.frozen').each(function (tdIndex, tdItem) {
					jQuery(tdItem).css('left', prevTdWidth);
					prevTdWidth += jQuery(tdItem).outerWidth() ?? 0;
				});
			});
		}, 100);
	};
	handleSticky();

	const resetTargetCheckeboxes = ($targetRef) => {
		if ($targetRef?.current) {
			const selectedItems: NodeListOf<HTMLLabelElement> = $targetRef.current.querySelectorAll('.m-checkbox--add input:checked + label');
			selectedItems.forEach((bearingInput) => bearingInput.click());
		}
	};

	const resetSelectedBearings = () => {
		if (tableBodyRef?.current) {
			resetTargetCheckeboxes(tableBodyRef);
		}

		if (gridViewRef?.current) {
			resetTargetCheckeboxes(gridViewRef);
		}
	};

	const prepareBearingTypeList = () => {
		bearingTypeListForColumn = [];
		data.forEach((item) => {
			bearingTypeListForColumn.push(item.bearingType[0]);
		});
	};

	const prepareUpdatedBearingSearchTableHeads = (updatedGroup) => {
		updatedBearingSearchTableHeads = updatedGroup;
	};
	const handleBearingToggle = (bearing: BearingInfo) => {
		const projectBearing = createBearingSaveItem(bearing);

		const selected = selectedBearings.findIndex(
			(item) => item.marketPlaceDescription === projectBearing.marketPlaceDescription,
		);
		if (selected >= 0) {
			setSelectedBearings((prev) =>
				prev.filter((item) => item.marketPlaceDescription !== projectBearing.marketPlaceDescription));
		} else {
			setSelectedBearings((prev) => [...prev, projectBearing]);
		}
	};

	const buildBearingSearchTableHeaders = (head: BearingTableHead) => {
		const { key, label, labelUnit, showLabelUnit, sortable } = head;

		const { sortDirection, sortKey } = refinementState;

		const isAuthorizedOnlyColumn = key === 'relPrice' || key === 'availability';
		prepareBearingTypeList();
		const showCeRatingCol = ballBearingTypes.some((el) => bearingTypeListForColumn.includes(el));

		if ((key.toLowerCase() === 'cerating' && !showCeRatingCol) ||
			((key.toLowerCase() === 'innerringpartnumber' || key.toLowerCase() === 'outerringpartnumber') && !bearingTypeList.includes('TRB'))) {
			return null;
		}

		return (
			<th
				scope="col"
				key={`bearing_search_results_head_${key.toLowerCase()}`}
				className={`col--${key.toLowerCase()} ${isAuthorizedOnlyColumn && !isAuthorized ? ' is--disabled ' : ''} ${key.toLowerCase() === 'marketplacedescription' ? 'frozen-th frozen__col--3' : ''} ${shouldBeCenterAligned(label, 'head', leftAlignedHeaders, undefined)}`}
			>
				<div className={isAuthorizedOnlyColumn && !isAuthorized ? 'is--authenticated-only' : ''}>
					{label} {showLabelUnit && labelUnit && ' (' + labelUnit[unit] + ')'}
					{getTooltip(label) &&
						<Tooltip content={getTooltip(label)} className='a-tooltip__text a-tooltip__text--bottom'></Tooltip>
					}

					{sortable && totalRecords > 1 && (
						<SortButton
							sortKey={key}
							direction={
								key === sortKey
									? sortDirection
									: undefined
							}
							isActive={key === sortKey}
							onSort={(newSortKey) =>
								dispatch({
									type: 'sort_by',
									payload: newSortKey as BearingKey,
								})
							}
						/>
					)}
				</div>
			</th>
		);
	};

	const buildBearingSearchTableBodyRow = (row, rowIndex) => {
		const marketPlaceDescription =
			row.marketPlaceDescription || '';
		const rowKey = generateId(
			`${marketPlaceDescription || rowIndex}`,
		);
		const detailsUrl = getBearingDetailsUrl(row);
		const showCeRatingData = ballBearingTypes.some((el) => bearingTypeListForColumn.includes(el));

		return (
			<tr key={`bearing_search_row_${rowKey}`}>
				<td className={'frozen frozen__col--0'}>
					<div className={isInComparedData(marketPlaceDescription) ? 'm-checkbox m-checkbox--add m-checkbox--checked' : 'm-checkbox m-checkbox--add'}>
						<input
							id={`bearing_search_compare_${rowKey}`}
							className="m-checkbox__input"
							type="checkbox"
							aria-label={`${text.addToCompare}${marketPlaceDescription}`}
							name={`bearing_search_compare_${rowKey}`}
							value={`bearing_search_compare_${rowKey}`}
							checked={isInComparedData(
								marketPlaceDescription,
							)}
							disabled={
								!isInComparedData(
									marketPlaceDescription,
								) &&
								refinementState.comparedData.length >=
								COMPARE_SIZE
							}
							onChange={() =>
								dispatch({
									type: 'update_compare',
									payload: row,
								})
							}
						/>
						<label
							className="m-checkbox__label"
							htmlFor={`bearing_search_compare_${rowKey}`}
						></label>
					</div>
				</td>
				<td className={'frozen frozen__col--save frozen__col--1'}>
					<CheckboxPlus disabled={!isAuthenticated || !isSsoEnabled} index={rowIndex} handleOnChange={() => {
						handleBearingToggle(row);
					}} />
				</td>

				<td className={'frozen frozen__col--2'}>
					<a
						className="btn btn--tertiary"
						href={detailsUrl}
						target="_blank"
						rel="noreferrer"
						onClick={() =>
							handleProductClick(row, rowIndex)
						}
					>
						{text.viewDetails}
					</a>
				</td>

				{bearingSearchTableHeads.map((column) => {
					// availability and price columns
					const colKey = generateId(column.key);
					const isAuthorizedOnlyColumn = column.key === 'relPrice' || column.key === 'availability';
					if (isAuthorizedOnlyColumn) {
						if (isAuthorized) {
							if (column.key === 'relPrice') {
								return (
									buildBasicDataCell(`is--price is--price-${row[column.key]?.length}`, '', column)
								);
							}
							if (column.key === 'availability') {
								return (
									<td key={`bearing_search_data_${colKey}`} className={`${shouldBeCenterAligned(column.label, 'head', leftAlignedHeaders, undefined)}`}>
										<AvailabilityBar
											availability={row[column.key]?.length || 0}
										/>
									</td>
								);
							}
						}
						return (
							buildBasicDataCell(`${!isAuthorized ? 'is--disabled ' : ''}`, column.key === 'relPrice' ? '$ -- --' : '-- -- --', column)
						);
					}
					const colKeyLowerCase = colKey.toLowerCase();
					// bearing type columns
					if (bearingTypeList.includes('TRB')) {
						if ((colKeyLowerCase === 'innerringpartnumber' || colKeyLowerCase === 'outerringpartnumber') && !(row.bearingType && row.bearingType.includes('TRB'))) {
							return buildBearingSearchDataCell(column);
						}
						if (colKeyLowerCase === 'cerating') {
							if (!showCeRatingData) {
								return null;
							}
							if (!ballBearingTypes.some((el) => row.bearingType?.includes(el))) {
								return buildBearingSearchDataCell(column);
							}
						}
						return buildBearingSearchDataCell(column, row[column.key]?.toString());
					}
					if (colKeyLowerCase === 'cerating') {
						if (!showCeRatingData) {
							return null;
						}
						if (!ballBearingTypes.some((el) => row.bearingType?.includes(el))) {
							return buildBearingSearchDataCell(column);
						}
					}
					if (colKeyLowerCase !== 'innerringpartnumber' && colKeyLowerCase !== 'outerringpartnumber') {
						return buildBearingSearchDataCell(column, row[column.key]?.toString());
					}
					return null;
				})}
			</tr>
		);
	};

	const buildBearingSearchDataCell = (column: BearingTableHead, content?: string | number) => {
		const colKey = generateId(column.key);
		return (
			<td key={`bearing_search_data_${colKey}`}
				className={`${colKey.toLowerCase() === 'marketplacedescription' ? 'frozen frozen__col--3' : ''}${shouldBeCenterAligned(column.label, 'head', leftAlignedHeaders, undefined)}`}>
				{content || ''}
			</td>
		);
	};

	const buildTooltipGridRows = (column: BearingTableHead, content?: string | number, cellClass = '', headerClass = '', rowClass = '') => {
		const colKey = generateId(column.key);
		return <tr key={colKey} className={rowClass}>
			<th scope="row" className={headerClass}>
				{column.label}
				<Tooltip content={getTooltip(column.label)} className='a-tooltip__text a-tooltip__text--bottom'></Tooltip>
			</th>
			<td className={cellClass}>
				{column.key === 'availability' && (
					<AvailabilityBar availability={content as number} />
				)}
				{column.key !== 'availability' && content && `${content}`}
			</td>
		</tr>;
	};

	const buildGridRows = (column: BearingTableHead, content = '') => {
		const colKey = generateId(column.key);
		return (
			<tr key={colKey}>
				<th scope="row">{column.label} {column.showLabelUnit && column.labelUnit && ' (' + column.labelUnit[unit] + ')'}</th>
				<td>{content}</td>
			</tr>
		);
	};

	const buildBearingGridList = (row, rowIndex) => {
		const marketPlaceDescription =
			row.marketPlaceDescription || '';
		const rowKey = generateId(
			`${marketPlaceDescription || rowIndex}`,
		);
		const detailsUrl = getBearingDetailsUrl(row);

		// Split into two columns
		let columnLen = bearingSearchTableHeads.length;
		const updatedGroup = bearingSearchTableHeads.slice(0, columnLen);

		// Remove CeRating if it doesn't include a BB Part
		if (!ballBearingTypes.some((el) => row.bearingType?.includes(el))) {
			if (row.ceRating) {
				updatedGroup.splice(updatedGroup.findIndex((item) => item.key === 'ceRating'), 1);
				columnLen -= 1;
			}
		}

		if (!row.bearingType?.includes('TRB')) {
			if (row.innerRingPartNumber) {
				updatedGroup.splice(updatedGroup.findIndex((item) => item.key === 'innerRingPartNumber'), 1);
				columnLen -= 1;
			}
			if (row.outerRingPartNumber) {
				updatedGroup.splice(updatedGroup.findIndex((item) => item.key === 'outerRingPartNumber'), 1);
				columnLen -= 1;
			}
		}

		prepareUpdatedBearingSearchTableHeads(updatedGroup);
		const middle = Math.ceil(columnLen / 2);
		const tableHeadGroup1 = updatedGroup.slice(0, middle);
		const tableHeadGroup2 = updatedGroup.slice(
			middle,
			columnLen,
		);

		return (
			<li key={rowKey} className="c-bearings-grid__item">
				<div className="c-bearings-grid__head">
					<h5>{marketPlaceDescription}</h5>

					{!!detailsUrl && (
						<a
							className="btn btn--tertiary"
							href={detailsUrl}
							target="_blank"
							rel="noreferrer"
							onClick={() =>
								handleProductClick(row, rowIndex)
							}
						>
							{text.viewDetails}
						</a>
					)}

					<table className="ms-md-auto">
						<tbody>
							<tr>
								<td>
									<div className={isInComparedData(marketPlaceDescription) ? 'm-checkbox m-checkbox--add m-checkbox--checked' : 'm-checkbox m-checkbox--add'}>
										<input
											id={`bearing_search_compare_${rowKey}`}
											className="m-checkbox__input"
											type="checkbox"
											aria-label={`${text.addToCompare}${marketPlaceDescription}`}
											name={`bearing_search_compare_${rowKey}`}
											value={`bearing_search_compare_${rowKey}`}
											checked={isInComparedData(
												marketPlaceDescription,
											)}
											disabled={
												isInComparedData(
													marketPlaceDescription,
												)
													? false
													: refinementState.comparedData
														.length >= COMPARE_SIZE
											}
											onChange={() =>
												dispatch({
													type: 'update_compare',
													payload: row,
												})
											}
										/>
										<label
											className="m-checkbox__label"
											htmlFor={`bearing_search_compare_${rowKey}`}
										></label>
									</div>

								</td>
								<td>
									<label
										htmlFor={`bearing_search_compare_${rowKey}`}
										// eslint-disable-next-line no-nested-ternary
										className={`btn btn--tertiary ${!isInComparedData(
											marketPlaceDescription,
										) &&
											refinementState.comparedData.length >=
											COMPARE_SIZE
											? 'disabled'
											: ''
											}`}
									>
										{!isInComparedData(marketPlaceDescription) ? text.addToCompareLabel : text.removeFromCompareLabel}
									</label>
								</td>

							</tr>
							<tr>
								<td>
									<CheckboxPlus disabled={!isAuthenticated || !isSsoEnabled} index={rowKey} handleOnChange={() => {
										handleBearingToggle(row);
									}} />
								</td>
								<td>
									<label
										htmlFor={`checkbox_${rowKey}`}
										className={`btn btn--tertiary m-checkbox__label--add ${!isAuthenticated || !isSsoEnabled ? 'disabled' : ''}`}
									>
										Add to Project
									</label>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div className="row">
					<div className="col-md-6 col-lg-4">
						<table>
							<tbody>
								{tableHeadGroup1.map((_, colIndex) => {
									const column =
										updatedBearingSearchTableHeads[colIndex];

									const isAuthorizedOnlyColumn = column.key === 'relPrice' || column.key === 'availability';

									if (isAuthorizedOnlyColumn) {
										if (isAuthorized) {
											if (column.key === 'relPrice') {
												return (
													buildTooltipGridRows(column, row[column.key]?.toString(), `is--price is--price-${row[column.key]?.length}`)
												);
											}
											if (column.key === 'availability') {
												return (
													buildTooltipGridRows(column, row[column.key]?.length, '')
												);
											}
										}
										return (
											buildTooltipGridRows(column, column.key === 'relPrice' ? '$ -- --' : '-- -- --', '', 'is--authenticated-only', !isAuthorized ? 'is--disabled ' : '')
										);
									}

									return (
										buildGridRows(column, row[column.key]?.toString())
									);
								})}
							</tbody>
						</table>
					</div>

					<div className="offset-md-1 col-md-5 col-lg-4">
						<table>
							<tbody>
								{tableHeadGroup2.map((_, colIndex) => {
									const index = middle + colIndex;

									const column =
										updatedBearingSearchTableHeads[
										index
										];

									return (
										buildGridRows(column, row[column.key]?.toString())
									);
								})}
							</tbody>
						</table>
					</div>
				</div>
			</li>
		);
	};

	const buildBasicDataCell = (className = '', content = '', column: BearingTableHead) => {
		const colKey = generateId(column.key);
		return (
			<td
				key={`bearing_search_data_${colKey}`}
				className={`${className}${shouldBeCenterAligned(column.label, 'head', leftAlignedHeaders, undefined)}`}
			>
				{content}
			</td>
		);
	};

	const toggleSearchView = (view: View) => {
		const shouldResetBearings = view !== refinementState.view;

		if (shouldResetBearings) {
			resetSelectedBearings();
		}
		handleSticky();
		dispatch({ type: 'change_view', payload: view });
	};

	return (
		<div className="t-engineering-tools__results">
			<h2>
				{!loading && totalRecords === 0 ? text.noBearingResults : text.results}
			</h2>

			{loading && <TableLoader />}

			{!loading && (
				<>
					{totalRecords === 0 && (
						<div className="t-engineering-tools__bearing-search__no-results">
							<div className='t-engineering-tools__bearing-search__no-results--suggestions'>
								<h5>Search Suggestions</h5>
								<ul className="t-engineering-tools__no-results-list t-engineering-tools__no-results-list--suggestions">
									<li>Choose only one bearing type to see search results</li>
									<li>Double check you are entering a Timken part number in the search.</li>
									<li>Check spelling of part number</li>
									<li>Apply one filter at a time</li>
									<li>Use fewer numbers</li>
								</ul>
							</div>
							<div className='t-engineering-tools__bearing-search__no-results--help'>
								<h5>Additional Help</h5>
								<ul className="t-engineering-tools__bearing-search__no-results-list t-engineering-tools__bearing-search__no-results-list--help">
									<li>
										<a href={guidedHelpUrls.contactUs} target="_blank" rel="noreferrer" className='btn btn--tertiary'>
											{text.contactUs}
										</a>
									</li>
									<li>
										<a href="https://youtu.be/KxupJT6uGMg?si=XD1F-LL1ovO0wPOd" target="_blank" rel="noreferrer" className='btn btn--tertiary'>
											How to search for products
										</a>
									</li>
								</ul>
							</div>
						</div>
					)}

					{totalRecords > 0 && (
						<>
							<AuthenticationBar useAlternateAuthCheck={true} isDetailsPage={true} customClassName='m-auto' isBearing={true} />

							<div className="t-engineering-tools__view-bar">
								<SearchPaginationLabel
									current={refinementState.currentPage}
									total={totalRecords}
									pageSize={refinementState.pageSize}
								/>

								<div className="t-engineering-tools__view-toggle">
									<button className="a-icon-cta" aria-label={text.list} onClick={() => toggleSearchView(View.LIST)
									}>

										<Icon icon={`${refinementState.view === View.LIST ? 'list-fill'
											: 'list-outline'}`} />
									</button>

									<button className="a-icon-cta" aria-label={text.list} onClick={() => toggleSearchView(View.GRID)
									}>

										<Icon icon={`${refinementState.view === View.GRID ? 'grid-fill'
											: 'grid-outline'}`} />
									</button>
								</div>
							</div>
							{/* standard table/list view */}
							{refinementState.view === View.LIST && (
								<div className="t-engineering-tools__table">
									{refinementState.isRefining && (
										<div className="t-engineering-tools__table-overlay">
											<Loader />
										</div>
									)}
									<div className="c-bearings-table">
										<table>
											<caption className="sr-only">{text.results}</caption>
											<thead>
												<tr>
													<th scope="col" className='frozen-th frozen__col--0'>{text.compare}</th>
													<th scope="col" className={`frozen-th frozen__col--1 frozen__col--save ${!isAuthenticated ? 'is--disabled ' : ''}`}>
														{!isAuthenticated && (
															<Icon icon="lock" className='me-1 is--disabled' />
														)
														}
														{text.save}
													</th>
													<th scope="col" className='frozen-th frozen__col--2'>{text.productDetails}</th>
													{/* Build table headers */}
													{bearingSearchTableHeads.map((head: BearingTableHead) => buildBearingSearchTableHeaders(head))}
												</tr>
											</thead>
											<tbody ref={tableBodyRef}>
												{/* Build table body rows */}
												{data.map((row, rowIndex) => buildBearingSearchTableBodyRow(row, rowIndex))}
											</tbody>
										</table>
									</div>
								</div>
							)}
							{/* Grid view */}
							{refinementState.view === View.GRID && (
								<div className="c-bearings-grid" ref={gridViewRef}>
									{refinementState.isRefining && (
										<div className="t-engineering-tools__table-overlay">
											<Loader />
										</div>
									)}

									<ul className="c-bearings-grid__list">
										{/* buiild grid markup from bearing data */}
										{data.map((row, rowIndex) =>
											buildBearingGridList(row, rowIndex),
										)}
									</ul>
								</div>
							)}

							<div className="c-bearings-table__footer-actions">
								<div className="c-bearings-table__footer-cta">
									{refinementState.comparedData.length > 1 && compareUrl && (
										<a href={compareUrl} className="btn btn--primary">
											{text.compare}
										</a>
									)}
									<div className="timken-dropdown">
										<SaveMultiBearingDropdown
											buttonStyle='full'
											displayedUnit={unit}
											menuDirection='up'
											bearings={selectedBearings}
											tableBodyRef={tableBodyRef}
											gridViewRef={gridViewRef}
										></SaveMultiBearingDropdown>
									</div>
								</div>

								<div className="c-bearings-table__footer-pagination">
									<SearchPagination
										total={refinementState.totalPages}
										current={refinementState.currentPage}
										onNavigate={(page: number) =>
											dispatch({ type: 'change_page', payload: page })
										}
									/>
								</div>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
};

export default BearingSearchResults;
